/**
 * Provider della sessione.
 *
 * All'avvio rimette in memoria il token salvato e mostra subito il profilo
 * memorizzato — cosi' l'interfaccia non sfarfalla verso la pagina di accesso a
 * ogni ricaricamento — ma **subito dopo lo verifica** con `GET /auth/me`. Se il
 * server risponde 401 la custodia ha gia' chiuso la sessione e qui si azzera lo
 * stato; se risponde con un profilo diverso (ruolo cambiato, nodi
 * riassegnati) vince quello del server.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  accedi as accediApi,
  esci as esciApi,
  ErroreApi,
  impersonifica as impersonificaApi,
  profiloCorrente,
} from "../api/client";
import {
  aggiornaProfiloMemorizzato,
  iniziaImpersonificazione,
  osservaDecadenzaSessione,
  ripristinaSessione,
  terminaImpersonificazione as terminaImpersonificazioneSessione,
} from "../api/session";
import type { UtenteCorrente } from "../api/types";
import { ContestoAutenticazione } from "./contesto";
import type { ValoreAutenticazione } from "./contesto";

export function FornitoreAutenticazione({ children }: { children: ReactNode }) {
  // Il promemoria si legge **durante l'inizializzazione**, non dentro un
  // effetto. Farlo nell'effetto significava partire da `null`, rendere una
  // volta come se non ci fosse sessione, e solo allora riportare il profilo:
  // un render in piu' a ogni avvio, e una finestra in cui l'applicazione
  // credeva di non avere nessuno collegato. L'inizializzatore pigro gira una
  // sola volta per montaggio e `ripristinaSessione` e' idempotente — legge
  // `localStorage` e rimette il token in memoria — quindi il doppio invito
  // dello StrictMode in sviluppo non cambia nulla.
  const [sessioneRipristinata] = useState<UtenteCorrente | null>(() =>
    ripristinaSessione(),
  );
  const [utente, setUtente] = useState<UtenteCorrente | null>(sessioneRipristinata);
  // Nessun promemoria da confermare: non c'e' alcun avvio da attendere.
  const [inAvvio, setInAvvio] = useState(sessioneRipristinata !== null);

  // Il profilo ripristinato e' **provvisorio** finche' `GET /auth/me` non lo
  // conferma: ruolo e nodi assegnati possono essere cambiati lato server.
  useEffect(() => {
    if (!sessioneRipristinata) return;

    const controllore = new AbortController();
    let attivo = true;

    profiloCorrente(controllore.signal)
      .then((confermato) => {
        if (!attivo) return;
        setUtente(confermato);
        aggiornaProfiloMemorizzato(confermato);
      })
      .catch((causa: unknown) => {
        if (!attivo || controllore.signal.aborted) return;
        // 401: la custodia ha gia' ripulito, qui si allinea lo stato locale.
        // Altri errori (API spenta, rete assente): si mantiene il profilo
        // memorizzato, e sara' la prima richiesta utile a far cadere la sessione.
        if (causa instanceof ErroreApi && causa.stato === 401) setUtente(null);
      })
      .finally(() => {
        if (attivo) setInAvvio(false);
      });

    return () => {
      attivo = false;
      controllore.abort();
    };
  }, [sessioneRipristinata]);

  // Una risposta 401 su una richiesta qualsiasi chiude la sessione: qui si
  // reagisce senza che ogni pagina debba occuparsene.
  useEffect(() => osservaDecadenzaSessione(() => setUtente(null)), []);

  const accedi = useCallback(async (nomeUtente: string, password: string) => {
    const risposta = await accediApi({ nome_utente: nomeUtente, password });
    setUtente(risposta.utente);
  }, []);

  /**
   * Passa nei panni di un'altra utenza.
   *
   * La sessione dell'amministratore non si chiude: si mette da parte, ed e' cio'
   * che rende il ritorno un gesto invece di un nuovo accesso. Se non si riesce a
   * conservarla — storage non disponibile — **non si entra**: restare nei panni
   * di un altro senza poter tornare e' il peggiore dei due esiti.
   */
  const impersonifica = useCallback(async (idUtente: number) => {
    const risposta = await impersonificaApi(idUtente);
    if (!iniziaImpersonificazione(risposta)) {
      throw new ErroreApi(
        "ERRORE_INTERNO",
        "Non e' stato possibile conservare la sessione corrente: l'impersonificazione non e' stata avviata, per non lasciarti senza una via di ritorno.",
        0,
      );
    }
    setUtente(risposta.utente);
  }, []);

  /** Torna alla propria utenza; se non c'e' piu' nulla da ripristinare, esce. */
  const terminaImpersonificazione = useCallback(() => {
    const propria = terminaImpersonificazioneSessione();
    if (!propria) {
      esciApi();
      setUtente(null);
      return;
    }
    setUtente(propria);
    // Il profilo memorizzato e' quello di prima: lo si riconferma dal server,
    // perche' nel frattempo ruolo o perimetro possono essere cambiati.
    profiloCorrente()
      .then((confermato) => {
        setUtente(confermato);
        aggiornaProfiloMemorizzato(confermato);
      })
      .catch(() => {
        // Si resta sul profilo ripristinato: sara' la prima richiesta utile a
        // far cadere la sessione, se non vale piu'.
      });
  }, []);

  const esci = useCallback(() => {
    esciApi();
    setUtente(null);
  }, []);

  const valore = useMemo<ValoreAutenticazione>(
    () => ({
      utente,
      inAvvio,
      eSuperAdmin: utente?.ruolo === "super_admin",
      nodiAssegnati: utente?.nodi_assegnati ?? [],
      impersonatoDa: utente?.impersonato_da ?? null,
      impersonificazioneScadeIl: utente?.impersonificazione_scade_il ?? null,
      accedi,
      impersonifica,
      terminaImpersonificazione,
      esci,
    }),
    [utente, inAvvio, accedi, impersonifica, terminaImpersonificazione, esci],
  );

  return (
    <ContestoAutenticazione.Provider value={valore}>{children}</ContestoAutenticazione.Provider>
  );
}
